import React, { useState, useRef } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';
import SmartFormAssistant from '../../../components/ai/SmartFormAssistant';
import { trackFormSubmission, trackEvent } from '../../../hooks/useGoogleAnalytics';

const ContactForm = ({ onSubmit, isSubmitting }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    inquiryType: '',
    name: '',
    email: '',
    phone: '',
    address: '',
    serviceType: '',
    preferredContact: 'phone',
    urgency: 'normal',
    message: '',
    budget: '',
    propertyType: '',
    preferredDate: '',
    preferredTime: '',
    hasPhotos: false,
    marketingConsent: false
  });
  const [errors, setErrors] = useState({});
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const fileInputRef = useRef(null);
  const dragCounter = useRef(0);
  const [isDragging, setIsDragging] = useState(false);
  const [isValid, setIsValid] = useState(false);
  const [isSubmittingLocal, setIsSubmittingLocal] = useState(false);

  const inquiryTypeOptions = [
    { value: 'service-quote', label: 'Service Quote Request' },
    { value: 'general-inquiry', label: 'General Inquiry' },
    { value: 'complaint', label: 'Complaint or Issue' },
    { value: 'emergency', label: 'Emergency Service' },
    { value: 'partnership', label: 'Business Partnership' },
    { value: 'feedback', label: 'Feedback or Suggestion' }
  ];

  const serviceTypeOptions = [
    { value: 'house-cleaning', label: 'House Cleaning' },
    { value: 'office-cleaning', label: 'Office Cleaning' },
    { value: 'carpet-cleaning', label: 'Carpet Cleaning' },
    { value: 'deep-cleaning', label: 'Deep Cleaning' },
    { value: 'post-construction', label: 'Post-Construction Cleaning' },
    { value: 'disinfection', label: 'Disinfection Service' },
    { value: 'vinyl-flooring', label: 'Vinyl Flooring Installation' },
    { value: 'wooden-flooring', label: 'Wooden Flooring Installation' },
    { value: 'tile-flooring', label: 'Tile Flooring Installation' },
    { value: 'carpet-installation', label: 'Carpet Installation' }
  ];

  const contactMethodOptions = [
    { value: 'phone', label: 'Phone Call' },
    { value: 'email', label: 'Email' },
    { value: 'whatsapp', label: 'WhatsApp' },
    { value: 'text', label: 'Text Message' }
  ];

  const urgencyOptions = [
    { value: 'low', label: 'Low Priority (Within 1 week)' },
    { value: 'normal', label: 'Normal (Within 2-3 days)' },
    { value: 'high', label: 'High Priority (Within 24 hours)' },
    { value: 'emergency', label: 'Emergency (Immediate response)' }
  ];

  const propertyTypeOptions = [
    { value: 'residential', label: 'Residential Home' },
    { value: 'apartment', label: 'Apartment/Condo' },
    { value: 'office', label: 'Office Building' },
    { value: 'retail', label: 'Retail Space' },
    { value: 'warehouse', label: 'Warehouse/Industrial' },
    { value: 'other', label: 'Other' }
  ];

  const budgetOptions = [
    { value: 'under-100', label: 'Under $100' },
    { value: '100-250', label: '$100 - $250' },
    { value: '250-500', label: '$250 - $500' },
    { value: '500-1000', label: '$500 - $1,000' },
    { value: '1000-2500', label: '$1,000 - $2,500' },
    { value: 'over-2500', label: 'Over $2,500' },
    { value: 'not-sure', label: 'Not Sure' }
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Clear error when user starts typing
    if (errors?.[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }

    // AI-powered suggestions based on inquiry type
    if (field === 'inquiryType') {
      provideSuggestions(value);
    }
  };

  const provideSuggestions = (inquiryType) => {
    const suggestions = {
      'service-quote': {
        serviceType: 'house-cleaning',
        urgency: 'normal',
        preferredContact: 'phone'
      },
      'emergency': {
        urgency: 'emergency',
        preferredContact: 'phone'
      },
      'complaint': {
        urgency: 'high',
        preferredContact: 'phone'
      }
    };

    if (suggestions?.[inquiryType]) {
      setFormData(prev => ({
        ...prev,
        ...suggestions?.[inquiryType]
      }));
    }
  };

  const validateStep = (step) => {
    const newErrors = {};

    if (step === 1) {
      if (!formData?.inquiryType) {
        newErrors.inquiryType = 'Please select an inquiry type';
      }
      if (!formData?.name) {
        newErrors.name = 'Name is required';
      }
      if (!formData?.email) {
        newErrors.email = 'Email is required';
      } else if (!/\S+@\S+\.\S+/?.test(formData?.email)) {
        newErrors.email = 'Please enter a valid email';
      }
      if (!formData?.phone) {
        newErrors.phone = 'Phone number is required';
      }
    }

    if (step === 2 && (formData?.inquiryType === 'service-quote' || formData?.inquiryType === 'emergency')) {
      if (!formData?.serviceType) {
        newErrors.serviceType = 'Please select a service type';
      }
      if (!formData?.address) {
        newErrors.address = 'Address is required for service quotes';
      }
      if (!formData?.propertyType) {
        newErrors.propertyType = 'Please select property type';
      }
    }

    if (step === 3) {
      if (!formData?.message || formData?.message?.length < 10) {
        newErrors.message = 'Please provide more details (minimum 10 characters)';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const nextStep = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, 3));
    }
  };

  const prevStep = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const handleFileUpload = () => {
    fileInputRef?.current?.click();
  };

  const handleFileChange = (event) => {
    const files = Array.from(event?.target?.files);
    processFiles(files);
  };

  const processFiles = (files) => {
    const validFiles = files?.filter(file => {
      const isValidType = file?.type?.startsWith('image/') || file?.type === 'application/pdf';
      const isValidSize = file?.size <= 10 * 1024 * 1024; // 10MB
      return isValidType && isValidSize;
    });

    setUploadedFiles(prev => [...prev, ...validFiles?.slice(0, 5 - prev?.length)]);
    setFormData(prev => ({
      ...prev,
      hasPhotos: true
    }));
  };

  const removeFile = (index) => {
    setUploadedFiles(prev => prev?.filter((_, i) => i !== index));
    if (uploadedFiles?.length === 1) {
      setFormData(prev => ({
        ...prev,
        hasPhotos: false
      }));
    }
  };

  const handleDragEnter = (e) => {
    e?.preventDefault();
    dragCounter.current++;
    if (e?.dataTransfer?.items && e?.dataTransfer?.items?.length > 0) {
      setIsDragging(true);
    }
  };

  const handleDragLeave = (e) => {
    e?.preventDefault();
    dragCounter.current--;
    if (dragCounter?.current === 0) {
      setIsDragging(false);
    }
  };

  const handleDragOver = (e) => {
    e?.preventDefault();
  };

  const handleDrop = (e) => {
    e?.preventDefault();
    setIsDragging(false);
    dragCounter.current = 0;
    
    if (e?.dataTransfer?.files && e?.dataTransfer?.files?.length > 0) {
      const files = Array.from(e?.dataTransfer?.files);
      processFiles(files);
      e?.dataTransfer?.clearData();
    }
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setIsSubmittingLocal(true);
    
    // Track form submission
    trackFormSubmission('smart_contact_form', 'contact');
    
    try {
      if (validateStep(currentStep)) {
        const submissionData = {
          ...formData,
          files: uploadedFiles,
          timestamp: new Date()?.toISOString(),
          estimatedResponseTime: getEstimatedResponseTime()
        };
        onSubmit(submissionData);
        trackEvent('form_success', 'contact', 'smart_contact_completed');
      }
    } catch (error) {
      console.error('Form submission error:', error);
      trackEvent('form_error', 'contact', error?.message);
    } finally {
      setIsSubmittingLocal(false);
    }
  };

  const getEstimatedResponseTime = () => {
    const timeMap = {
      'emergency': '15 minutes',
      'high': '2-4 hours',
      'normal': '24-48 hours',
      'low': '2-3 business days'
    };
    return timeMap?.[formData?.urgency] || '24-48 hours';
  };

  const getProgressPercentage = () => {
    return (currentStep / 3) * 100;
  };

  const shouldShowServiceFields = () => {
    return formData?.inquiryType === 'service-quote' || formData?.inquiryType === 'emergency';
  };

  const handleAISuggestionApply = (suggestionType, value) => {
    switch (suggestionType) {
      case 'service':
        if (!formData?.serviceType?.includes(value)) {
          setFormData(prev => ({
            ...prev,
            serviceType: [...prev?.serviceType, value]
          }));
        }
        break;
      case 'frequency':
        setFormData(prev => ({
          ...prev,
          frequency: value
        }));
        break;
      default:
        console.log('Applied suggestion:', suggestionType, value);
    }
  };

  return (
    <div className="max-w-2xl mx-auto p-6 bg-white rounded-lg shadow-lg">
      {/* Progress Bar */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-text-primary">
            Step {currentStep} of 3
          </span>
          <span className="text-sm text-text-secondary">
            {Math.round(getProgressPercentage())}% Complete
          </span>
        </div>
        <div className="w-full bg-muted rounded-full h-2">
          <div 
            className="bg-primary h-2 rounded-full transition-all duration-300"
            style={{ width: `${getProgressPercentage()}%` }}
          />
        </div>
      </div>
      <SmartFormAssistant 
        currentFormData={formData}
        onSuggestionApply={handleAISuggestionApply}
      />
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Step 1: Basic Information */}
        {currentStep === 1 && (
          <div className="space-y-6">
            <div className="text-center mb-6">
              <h2 className="text-2xl font-bold text-text-primary mb-2">
                Let's Get Started
              </h2>
              <p className="text-text-secondary">
                Tell us about your inquiry and how we can help you
              </p>
            </div>

            <Select
              label="What can we help you with?"
              options={inquiryTypeOptions}
              value={formData?.inquiryType}
              onChange={(value) => handleInputChange('inquiryType', value)}
              error={errors?.inquiryType}
              placeholder="Select inquiry type"
              required
            />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="Full Name"
                type="text"
                value={formData?.name}
                onChange={(e) => handleInputChange('name', e?.target?.value)}
                error={errors?.name}
                placeholder="Enter your full name"
                required
              />
              <Input
                label="Phone Number"
                type="tel"
                value={formData?.phone}
                onChange={(e) => handleInputChange('phone', e?.target?.value)}
                error={errors?.phone}
                placeholder="(555) 123-4567"
                required
              />
            </div>

            <Input
              label="Email Address"
              type="email"
              value={formData?.email}
              onChange={(e) => handleInputChange('email', e?.target?.value)}
              error={errors?.email}
              placeholder="your.email@example.com"
              required
            />

            {formData?.inquiryType === 'emergency' && (
              <div className="bg-error/10 border border-error/20 rounded-lg p-4">
                <div className="flex items-center space-x-2 mb-2">
                  <Icon name="AlertTriangle" size={20} color="var(--color-error)" />
                  <span className="font-medium text-error">Emergency Service</span>
                </div>
                <p className="text-sm text-error/80">
                  For immediate assistance, please call us directly at (555) 123-4567
                </p>
              </div>
            )}
          </div>
        )}

        {/* Step 2: Service Details */}
        {currentStep === 2 && (
          <div className="space-y-6">
            <div className="text-center mb-6">
              <h2 className="text-2xl font-bold text-text-primary mb-2">
                Service Details
              </h2>
              <p className="text-text-secondary">
                Help us understand your specific needs
              </p>
            </div>

            {shouldShowServiceFields() && (
              <>
                <Select
                  label="Service Type"
                  options={serviceTypeOptions}
                  value={formData?.serviceType}
                  onChange={(value) => handleInputChange('serviceType', value)}
                  error={errors?.serviceType}
                  placeholder="Select service needed"
                  required
                />

                <Input
                  label="Service Address"
                  type="text"
                  value={formData?.address}
                  onChange={(e) => handleInputChange('address', e?.target?.value)}
                  error={errors?.address}
                  placeholder="Enter full address"
                  description="Google Places will help autocomplete your address"
                  required
                />

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Select
                    label="Property Type"
                    options={propertyTypeOptions}
                    value={formData?.propertyType}
                    onChange={(value) => handleInputChange('propertyType', value)}
                    error={errors?.propertyType}
                    placeholder="Select property type"
                    required
                  />
                  <Select
                    label="Budget Range"
                    options={budgetOptions}
                    value={formData?.budget}
                    onChange={(value) => handleInputChange('budget', value)}
                    placeholder="Select budget range"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Input
                    label="Preferred Date"
                    type="date"
                    value={formData?.preferredDate}
                    onChange={(e) => handleInputChange('preferredDate', e?.target?.value)}
                    min={new Date()?.toISOString()?.split('T')?.[0]}
                  />
                  <Select
                    label="Preferred Time"
                    options={[
                      { value: 'morning', label: 'Morning (8AM - 12PM)' },
                      { value: 'afternoon', label: 'Afternoon (12PM - 5PM)' },
                      { value: 'evening', label: 'Evening (5PM - 8PM)' },
                      { value: 'flexible', label: 'Flexible' }
                    ]}
                    value={formData?.preferredTime}
                    onChange={(value) => handleInputChange('preferredTime', value)}
                    placeholder="Select preferred time"
                  />
                </div>
              </>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Select
                label="Preferred Contact Method"
                options={contactMethodOptions}
                value={formData?.preferredContact}
                onChange={(value) => handleInputChange('preferredContact', value)}
                placeholder="How should we contact you?"
              />
              <Select
                label="Urgency Level"
                options={urgencyOptions}
                value={formData?.urgency}
                onChange={(value) => handleInputChange('urgency', value)}
                placeholder="Select urgency level"
              />
            </div>
          </div>
        )}

        {/* Step 3: Message and Files */}
        {currentStep === 3 && (
          <div className="space-y-6">
            <div className="text-center mb-6">
              <h2 className="text-2xl font-bold text-text-primary mb-2">
                Additional Details
              </h2>
              <p className="text-text-secondary">
                Provide more information and attach photos if needed
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-text-primary mb-2">
                Message <span className="text-error">*</span>
              </label>
              <textarea
                rows={6}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent resize-none text-sm ${
                  errors?.message ? 'border-error' : 'border-border'
                }`}
                placeholder="Please describe your cleaning needs, any specific requirements, problem areas, or questions you have..."
                value={formData?.message}
                onChange={(e) => handleInputChange('message', e?.target?.value)}
                maxLength={1000}
              />
              <div className="flex justify-between items-center mt-1">
                {errors?.message && (
                  <span className="text-sm text-error">{errors?.message}</span>
                )}
                <span className="text-xs text-text-secondary ml-auto">
                  {formData?.message?.length}/1000 characters
                </span>
              </div>
            </div>

            {/* File Upload Area */}
            <div>
              <label className="block text-sm font-medium text-text-primary mb-2">
                Photos (Optional)
              </label>
              <div
                className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors duration-200 ${
                  isDragging 
                    ? 'border-primary bg-primary/5' :'border-border hover:border-primary/50'
                }`}
                onDragEnter={handleDragEnter}
                onDragLeave={handleDragLeave}
                onDragOver={handleDragOver}
                onDrop={handleDrop}
              >
                <Icon name="Upload" size={32} color="var(--color-text-secondary)" className="mx-auto mb-3" />
                <p className="text-text-primary font-medium mb-1">
                  Drop photos here or click to upload
                </p>
                <p className="text-sm text-text-secondary mb-4">
                  Upload up to 5 photos (JPG, PNG, PDF - Max 10MB each)
                </p>
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleFileUpload}
                  iconName="Camera"
                  iconPosition="left"
                >
                  Choose Files
                </Button>
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  accept="image/*,.pdf"
                  onChange={handleFileChange}
                  className="hidden"
                />
              </div>

              {/* Uploaded Files */}
              {uploadedFiles?.length > 0 && (
                <div className="mt-4 space-y-2">
                  <p className="text-sm font-medium text-text-primary">
                    Uploaded Files ({uploadedFiles?.length}/5)
                  </p>
                  {uploadedFiles?.map((file, index) => (
                    <div key={index} className="flex items-center justify-between p-3 bg-muted rounded-lg">
                      <div className="flex items-center space-x-3">
                        <Icon name="File" size={16} color="var(--color-text-secondary)" />
                        <div>
                          <p className="text-sm font-medium text-text-primary truncate">
                            {file?.name}
                          </p>
                          <p className="text-xs text-text-secondary">
                            {(file?.size / 1024 / 1024)?.toFixed(2)} MB
                          </p>
                        </div>
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => removeFile(index)}
                        className="text-text-secondary hover:text-error"
                      >
                        <Icon name="X" size={16} />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Consent Checkboxes */}
            <div className="space-y-3 pt-4 border-t border-border">
              <Checkbox
                label="I consent to receive marketing communications from Clean360"
                checked={formData?.marketingConsent}
                onChange={(e) => handleInputChange('marketingConsent', e?.target?.checked)}
                description="You can unsubscribe at any time"
              />
            </div>

            {/* Estimated Response Time */}
            <div className="bg-accent/10 border border-accent/20 rounded-lg p-4">
              <div className="flex items-center space-x-2 mb-2">
                <Icon name="Clock" size={20} color="var(--color-accent)" />
                <span className="font-medium text-accent">Estimated Response Time</span>
              </div>
              <p className="text-sm text-accent/80">
                Based on your urgency level, we'll respond within {getEstimatedResponseTime()}
              </p>
            </div>
          </div>
        )}

        {/* Navigation Buttons */}
        <div className="flex justify-between pt-6 border-t border-border">
          <Button
            type="button"
            variant="outline"
            onClick={prevStep}
            disabled={currentStep === 1}
            iconName="ArrowLeft"
            iconPosition="left"
          >
            Previous
          </Button>

          {currentStep < 3 ? (
            <Button
              type="button"
              variant="default"
              onClick={nextStep}
              iconName="ArrowRight"
              iconPosition="right"
            >
              Next Step
            </Button>
          ) : (
            <button
              type="submit"
              disabled={isSubmittingLocal || !isValid}
              onClick={() => trackEvent('form_submit_attempt', 'contact', 'smart_contact')}
              className="w-full bg-gradient-to-r from-blue-600 to-blue-700 text-white py-3 px-6 rounded-lg font-semibold hover:from-blue-700 hover:to-blue-800 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 flex items-center justify-center gap-2"
            >
              Submit Request
            </button>
          )}
        </div>
      </form>
    </div>
  );
};

export default ContactForm;